<template>
  <Layout>
    <div class="px-2">
      <div class="lg:flex pt-6 pb-8">
        <div class="w-full lg:w-1/3">
          <h2 class="pb-4">Users</h2>
        </div>
        <div class="w-full lg:w-2/3 lg:flex lg:justify-end">
          <div class="w-full lg:w-1/4 lg:mr-4 pb-4">
            <pt-form-input
              id="search-input"
              v-model="searchText"
              type="text"
              icon-class="fa-search"
              icon-position="left"
              placeholder="Enter search text..."
              @keyup.enter.native="searchUser"
            />
          </div>
          <div class="w-full lg:w-1/4 pb-2">
            <pt-button
              :is-full-width="true"
              variant="primary-outline"
              class="custom-download-button"
              @action="addNewUser"
            >
              <i class="fa fa-plus" />
              New User
            </pt-button>
          </div>
        </div>
      </div>
      <div class="flex">
        <pt-view-table
          :data="users"
          :columns="columns"
          :loading="tableLoader"
          :row-class="classes.row"
          :header-class="classes.header"
          :column-class="classes.column"
          :tbody-class="classes.tbody"
          :thead-class="classes.thead"
          class="promo-table shadow border-t"
        />
      </div>
      <div class="flex">
        <pt-pagination
          :total-rows="userCount"
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 15, 20]"
          class="mt-8 mb-16"
          @page-changed="pageLimitHandler"
        />
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@layouts/main';
import { PtButton } from '@pathao/ui-components/esm/Button';
import { PtFormInput } from '@pathao/ui-components/esm/FormComponents';
import { PtPagination } from '@pathao/ui-components/esm/Pagination';
import { PtViewTable } from '@pathao/ui-components/esm/Table';
// import { PtDatepicker } from '@pathao/ui-components/esm/Datepicker';
import { ADMIN_MANAGE_RESTO, USER_LIST } from '@src/config/apiEndpoints';
import apiRequest from '@src/utils/apiRequest';
import { format, parse } from 'date-fns';

const columns = [
  {
    header: 'ID',
    accessor: 'id',
  },
  {
    header: 'Parse ID',
    accessor: 'parse_id',
  },
  {
    header: 'Resto ID',
    accessor: 'resto_id',
  },
  {
    header: 'Type',
    component: {
      props: ['rowData'],
      render() {
        const adminType = this.rowData.is_resto_admin ? 'Resto Admin' : 'User Admin';
        return <div>{adminType}</div>;
      },
    },
  },
  {
    header: 'Name',
    accessor: 'name',
  },
  {
    header: 'Number',
    accessor: 'number',
  },
  {
    header: 'Email',
    accessor: 'email',
  },
  {
    header: 'Created At',
    component: {
      props: ['rowData'],
      render() {
        return (
          <div>
            <span>{format(parse(this.rowData.created_at), 'MMM DD, YYYY hh:MM a')}</span>
          </div>
        );
      },
    },
  },
];

export default {
  name: 'Users',
  page: {
    title: 'Users',
  },
  components: {
    Layout,
    PtViewTable,
    PtPagination,
    PtButton,
    PtFormInput,
  },
  data() {
    const date = new Date();
    date.setDate(date.getDate() - 15);
    return {
      dateRange: {
        from: date,
        to: new Date(),
      },
      searchText: '',
      users: [],
      tableLoader: false,
      isSearchOnce: false,
      isSearching: false,
      userCount: 5,
      currentPage: 1,
      pageSize: 20,
      classes: {
        thead: 'bg-grey-lighter w-full',
        tbody: 'bg-white text-center',
        row: 'border-b-4 lg:border-b border-solid border-grey-light mb-0 w-full',
        column: 'p-4 border-b lg:border-b-0 break-words', // border-r border-solid
        header: 'p-4',
      },
      columns: [
        ...columns,
        {
          header: 'Actions',
          props: row => ({
            handleUpdate: () => this.updateUser(row),
            enableResto: () => this.enableResto(row),
            ...row,
          }),
          component: {
            props: ['rowData'],
            render(h) {
              /*
              const enableBtn = !this.rowData.is_resto_admin ? null : (
                <button class="pt-btn pt-btn pt-btn-secondary pt-btn-sm mr-1 h-auto" onClick={this.rowData.enableResto}>
                  Toggle Order Acceptance
                </button>
              );
              */

              return (
                <div class="flex flex-col items-center">
                  <button
                    class="pt-btn pt-btn-outline pt-btn-primary-outline pt-btn-sm mr-1 mb-2"
                    onClick={this.rowData.handleUpdate}
                  >
                    Update
                  </button>
                </div>
              );
            },
          },
        },
      ], // test
    };
  },
  watch: {
    searchText: function(newVal) {
      if (!newVal && this.isSearchOnce) {
        this.getUsers();
        this.isSearchOnce = false;
      }
    },
  },
  created() {
    this.getUsers();
  },
  methods: {
    updateUser(userObject) {
      this.$router.push('/users/edit/' + userObject.id);
    },
    enableResto(userObject) {
      if (!userObject.resto_id) {
        this.$notification.warning('Resto ID is not present for User');
      }

      const url = ADMIN_MANAGE_RESTO.replace(':id', userObject.resto_id);

      apiRequest({
        url,
        method: 'PATCH',
        data: {
          enable: userObject,
        },
      });
    },
    addNewUser() {
      this.$router.push('/users/create');
    },
    searchUser() {
      this.isSearching = true;
      this.getUsers();
    },
    getUsers(page = '', pageSize = '') {
      let perPage = pageSize || 20;
      let searchParam = this.searchText;
      let startDate = format(new Date(), 'YYYY-MM-DD');
      let payload = {
        url:
          USER_LIST + 'page=' + page + '&per_page=' + perPage + '&search=' + searchParam + '&start_date=' + startDate,
        method: 'GET',
      };
      this.tableLoader = true;
      apiRequest(payload).then(response => {
        // console.log(response);
        this.tableLoader = false;
        if (this.isSearching) {
          this.isSearchOnce = true;
          this.isSearching = false;
        }
        this.users = response.data.users;
        this.userCount = response.data.records;
      });
    },
    pageLimitHandler(page, pageSize) {
      this.getUsers(page - 1, pageSize);
    },
  },
};
</script>

<style>
.vdp-datepicker__calendar {
  /*position: absolute;*/
  right: 0;
}
</style>
